import { useState } from 'react';
import { useEarnPoints } from '../queries';

type EarnPointsDrawerProps = {
  open: boolean;
  membershipUuid?: string;
  earnRatePaisePerPoint?: number;
  onClose: () => void;
};

const makeId = () => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

const EarnPointsDrawer = ({ open, membershipUuid, earnRatePaisePerPoint, onClose }: EarnPointsDrawerProps) => {
  const earnMutation = useEarnPoints();
  const [spendRupees, setSpendRupees] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [error, setError] = useState('');

  if (!open) return null;

  const spendPaise = Math.round((Number(spendRupees) || 0) * 100);
  const pointsToEarn = earnRatePaisePerPoint && earnRatePaisePerPoint > 0
    ? Math.floor(spendPaise / earnRatePaisePerPoint)
    : null;

  const handleSubmit = async () => {
    if (!membershipUuid) return;
    const rupees = Number(spendRupees);
    if (!Number.isFinite(rupees) || rupees <= 0) {
      setError('Enter a valid spend amount.');
      return;
    }
    setError('');
    try {
      await earnMutation.mutateAsync({
        membershipUuid,
        spendAmountPaise: Math.round(rupees * 100),
        sourceRef: sourceRef.trim() || undefined,
        idempotencyKey: `earn:${makeId()}`
      });
      setSpendRupees('');
      setSourceRef('');
      onClose();
    } catch {
      setError('Unable to record earn right now. Please try again.');
    }
  };

  return (
    <div className="membership-drawer-overlay" onClick={onClose}>
      <div className="membership-drawer-panel" onClick={(e) => e.stopPropagation()}>
        <div className="membership-drawer-panel__header">
          <h3>Earn points</h3>
          <button type="button" className="membership-drawer-panel__close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="membership-drawer-panel__form">
          <label className="field">
            <span className="label">Spend amount (₹)</span>
            <input
              className="input"
              type="number"
              min="0"
              step="1"
              value={spendRupees}
              onChange={(e) => setSpendRupees(e.target.value)}
              placeholder="e.g. 500"
            />
          </label>
          {pointsToEarn !== null && spendPaise > 0 ? (
            <p className="form-hint">Points to earn: <strong>{pointsToEarn}</strong></p>
          ) : null}
          <label className="field">
            <span className="label">Source ref <span className="label-optional">(optional)</span></span>
            <input
              className="input"
              value={sourceRef}
              onChange={(e) => setSourceRef(e.target.value)}
              placeholder="e.g. Invoice #123"
            />
          </label>
          {error ? <p className="form-hint danger">{error}</p> : null}
          <div className="membership-drawer-panel__actions">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="button" className="btn primary" onClick={handleSubmit} disabled={earnMutation.isPending}>
              {earnMutation.isPending ? 'Recording...' : 'Record earn'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarnPointsDrawer;
